/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService, ConfirmEventType, MenuItem} from 'primeng/api';
import {environment} from '../../../environments/environment';
import {User} from '../../shared/model/user';
import {BaseComponentDirective} from '../../base/base-component.directive';
import {HeaderComponent} from '../header/header.component';
import {NavItem} from '../header/nav-item';
import {UserService} from '../../shared/user.service';

@Component({
    selector: 'navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.less'],
    standalone: false
})
export class NavigationComponent extends BaseComponentDirective implements OnInit {
    @ViewChild('header') header: HeaderComponent;
    @Input() navMenuItems: NavItem[];

    title = 'eSCI Tools';
    loggedInUser: User;
    userName: string;
    userInitials = '';
    userProfileUrl = '';
    showTestUtils = false;

    userMenuItems: MenuItem[];
    helpMenuItems: MenuItem[];

    constructor(protected userService: UserService,
                private confirmationService: ConfirmationService,
                private router: Router) {
        super(userService);
        this.showTestUtils = !environment.production;
    }

    ngOnInit(): void {
        this.loggedInUser = this.userService.user;
        this.userName = this.loggedInUser.firstName + ' ' + this.loggedInUser.lastName;
        this.userInitials = this.loggedInUser.firstName.substring(0, 1) + this.loggedInUser.lastName.substring(0, 1);
        this.setMenuItems();
    }

    setMenuItems(): void {
        // User Menu
        const roles: MenuItem[] = [];
        this.loggedInUser.roles?.sort((a, b) => a.localeCompare(b));
        this.loggedInUser.roles?.forEach(r => {
            const roleMenuItem = {label: r, url: '', icon: 'pi pi-user', disabled: false};
            roles.push(roleMenuItem);
        });

        this.userMenuItems = [
            {
                label: 'Roles',
                items: roles
            },
        ];

        if (this.showTestUtils) {
            this.userMenuItems.push({
                label: 'Test Utilities',
                items: [
                    {
                        label: 'Sign Out',
                        icon: 'fa fa-fw fa-sign-out',
                        command: () => this.onLogout()
                    }
                ]
            });
        }

        const gmin = this.loggedInUser.gmin;
        this.userProfileUrl = 'https://peoplefinder.gm.com/photos/' + gmin.substring(0, 3) + '/' + gmin.substring(3, 6) + '/' + gmin + '_lthumb.jpg';

        // Help Menu
        this.helpMenuItems = [
            {
                label: 'eSCI Tools',
                key: '',
                id: 'esci',
                items: [
                    {
                        label: 'Soft Parts Release',
                        url: '',
                        key: '',
                        target: '_blank',
                        id: 'spr',
                        icon: 'pi-link',
                        separator: false
                    },
                    {
                        label: 'User Guide',
                        url: '',
                        key: '',
                        target: '_blank',
                        id: 'userGuide',
                        icon: 'fa-file-pdf-o',
                        separator: false
                    }
                ]
            },
            {
                label: 'Support', id: 'supportHeader', key: '', items: [
                    {
                        label: 'datafilereleasetoolhelp@gm.com',
                        id: 'email',
                        key: '',
                        icon: 'fa-envelope',
                        separator: false,
                        url: 'mailto:datafilereleasetoolhelp@gm.com'
                    },
                ], separator: false
            }
        ];

        // We will not use the help menu for now
        this.helpMenuItems.length = 0;
    }

    onProfilePicError() {
        this.userProfileUrl = '../../../assets/images/defaultProfilePic.svg';
    }

    onLogout() {
        this.confirmationService.confirm({
            message: 'Are you sure you want to log out of eSCI Tools?',
            accept: () => this.userService.logout(),
            reject: (type) => {
                if (type === ConfirmEventType.REJECT || type === ConfirmEventType.CANCEL) {
                    console.log('The User has decided NOT to log out');
                }
            }
        });
    }
}
